import { v4 } from "uuid";
import XLSX from "xlsx";
import moment from "moment/moment";
import { isMemberOfEU } from "../../../../../helpers/Country";

function parseHeaders(sheet, range, headerRowNum) {
    const headers = [];
    for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
        const cellAddress = XLSX.utils.encode_cell({
            r: headerRowNum,
            c: colNum,
        });
        const cell = sheet[cellAddress];
        headers[colNum] = cell ? cell.v : undefined;
    }
    return headers;
}

function getRowsGroupedByItemId(sheet, range, headerRowNum, headers) {
    const groupedRows = {};
    for (let rowNum = headerRowNum + 1; rowNum <= range.e.r; rowNum++) {
        let row = {};
        for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
            const cellAddress = XLSX.utils.encode_cell({
                r: rowNum,
                c: colNum,
            });
            const cell = sheet[cellAddress];
            row[headers[colNum]] = cell ? cell.v : null;
        }

        const itemId = row["Försändelsenummer"];
        if (!groupedRows[itemId]) {
            groupedRows[itemId] = [];
        }
        groupedRows[itemId].push(row);
    }
    return groupedRows;
}

function parseAdditionalRowData(row, vat) {
    const cost = parseFloat(row["Bruttopris"]) || 0;

    return {
        title: row["Beskrivning"],
        cost,
        vat,
        surcharge: true,
        key: v4(),
    };
}

function parseMainRowData(
    row,
    fileName,
    invoiceDate,
    invoiceNumber,
    vat,
    senderCountry,
    recipientCountry,
) {
    const formattedDate = XLSX.SSF.format(
        "yyyy-mm-dd hh:mm:ss",
        row["Mottaget av Bring datum och tid"],
    );
    const date = moment.utc(formattedDate).format("YYYY-MM-DD");

    return {
        fileName,
        transporter: "Bring",
        invoiceDate: invoiceDate,
        trackingNumber: row["Försändelsenummer"],
        reference: row["Avsändarens referens"]
            ? row["Avsändarens referens"]
            : "",
        currentInvoiceNumber: invoiceNumber,
        date,
        title: row["Beskrivning"],
        sender: row["Avsändarens namn"],
        recipient: row["Namn mottagare"],
        senderCountry,
        recipientCountry,
        vat,
    };
}

function parse({ workbook, fileName }) {
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    const headerRowNum = 12 - 1; // Subtract 1 because arrays are 0-based
    const headers = parseHeaders(sheet, range, headerRowNum);

    const groupedRows = getRowsGroupedByItemId(
        sheet,
        range,
        headerRowNum,
        headers,
    );

    let invoiceDate = null;
    let invoiceNumber = null;
    const invoiceData = [];
    for (const itemId in groupedRows) {
        const rows = groupedRows[itemId];

        let orderData;
        const orderRows = [];
        let totalShipmentCost = 0;
        let totalShipmentCostVat = 0;
        let totalShipmentCostNoVat = 0;
        rows.forEach((row) => {
            if (!invoiceDate) {
                const formattedDate = XLSX.SSF.format(
                    "yyyy-mm-dd hh:mm:ss",
                    row["Fakturadatum"],
                );
                invoiceDate = moment.utc(formattedDate).format("YYYY-MM-DD");
            }
            if (!invoiceNumber) {
                invoiceNumber = row["Fakturanummer"];
            }

            const senderCountry = row["Avsändarens landskod"];
            const recipientCountry = row["Mottagarens landskod"];
            const isEUShipment =
                isMemberOfEU(senderCountry) && isMemberOfEU(recipientCountry);
            const vatAmount = row["VAT (%)"];
            const vat = vatAmount && parseInt(vatAmount) > 0 && isEUShipment;

            const isShipmentMainRow = !!row["Fraktberäknad vikt, kg"];
            if (isShipmentMainRow) {
                // Handle the main shipment row
                orderData = parseMainRowData(
                    row,
                    fileName,
                    invoiceDate,
                    invoiceNumber,
                    vat,
                    senderCountry,
                    recipientCountry,
                );

                const grossAmount = parseFloat(row["Bruttopris"]) || 0;

                totalShipmentCost += grossAmount;
                if (vat) {
                    totalShipmentCostVat += grossAmount;
                } else {
                    totalShipmentCostNoVat += grossAmount;
                }

                orderRows.push({
                    title: `Fakturerad vikt: ${parseFloat(
                        row["Fraktberäknad vikt, kg"],
                    )} kg`,
                    key: v4(),
                });
            } else {
                // Handle as additional row-data
                const additionalRowData = parseAdditionalRowData(row, vat);
                orderRows.push(additionalRowData);

                totalShipmentCost += additionalRowData.cost;
                if (vat) {
                    totalShipmentCostVat += additionalRowData.cost;
                } else {
                    totalShipmentCostNoVat += additionalRowData.cost;
                }
            }
        });

        if (orderData) {
            orderData.totalShipmentCost = totalShipmentCost;
            orderData.totalShipmentCostVat = totalShipmentCostVat;
            orderData.totalShipmentCostNoVat = totalShipmentCostNoVat;
            orderData.rows = orderRows;
            invoiceData.push(orderData);
        }
    }

    return invoiceData;
}

export default {
    parse,
};
